const resources = {
  "en": {
    "home": {
      "title": "Welcome back",
      "description": "Pick up where you left off with quick links and upcoming deadlines.",
      "deadlineDateFormat": "MMM D, YYYY",
      "countdownFormat": "D [days] HH:mm:ss:SSS",
      "deadlinesTitle": "Top 5 deadlines",
      "deadlinesEmpty": "No deadlines yet",
      "manageDeadlines": "Open manager",
      "nextAction": {
        "title": "Next action",
        "loading": "Loading onboarding guidance...",
        "unavailable": "Onboarding guidance is temporarily unavailable.",
        "none": "You are all caught up for now.",
        "open": "Open"
      },
      "setupProgress": {
        "title": "Your setup progress",
        "loading": "Loading setup progress...",
        "unavailable": "Setup progress is temporarily unavailable.",
        "summary": "{{done}} / {{total}} completed",
        "goNext": "Go to next step",
        "help": {
          "buttonAria": "Help for {{step}}",
          "close": "Close",
          "accounts": {
            "title": "Add your base accounts",
            "why": "You need both account types before other money flows can be unlocked.",
            "steps": [
              "Go to Accounts and click Add account.",
              "Create one bank account.",
              "Create one cash account."
            ]
          },
          "liveBalanceAccounts": {
            "title": "Configure live balance sources",
            "why": "Live balances keep your current account amounts synced and improve forecast quality.",
            "steps": [
              "Open Profile setup.",
              "Find Live balance accounts settings.",
              "Connect at least one balance source and save."
            ]
          },
          "income": {
            "title": "Add first income",
            "why": "Income defines how money enters your system and is required for realistic planning.",
            "steps": [
              "Open Income.",
              "Click Add income and fill the form.",
              "Save one income transaction."
            ]
          },
          "expense": {
            "title": "Add first expense",
            "why": "Expenses define your outflow baseline and unlock recurring and goals scenarios.",
            "steps": [
              "Open Expenses.",
              "Click Add expense and enter amount/category.",
              "Save one expense transaction."
            ]
          },
          "recurring": {
            "title": "Add recurring transaction",
            "why": "Recurring data powers long-term cash-flow forecasting.",
            "steps": [
              "Open Recurring manager.",
              "Create recurring income or recurring expense.",
              "Set schedule and save."
            ]
          },
          "goals": {
            "title": "Create first financial goal",
            "why": "Goals connect your cash flow with clear long-term outcomes.",
            "steps": [
              "Open Finance goals.",
              "Set goal name, amount, and timeline.",
              "Save the goal."
            ]
          },
          "forecast": {
            "title": "Open forecast",
            "why": "Opening forecast confirms your baseline is complete and unlocks advanced insights.",
            "steps": [
              "Open Forecast from menu or Home CTA.",
              "Wait until forecast data is loaded.",
              "Review timeline and projections."
            ]
          }
        },
        "steps": {
          "accounts": "Add bank + cash accounts",
          "liveBalanceAccounts": "Configure live balance accounts",
          "income": "Add first income",
          "expense": "Add first expense",
          "recurring": "Add first recurring transaction",
          "goals": "Create first goal",
          "forecast": "Open forecast"
        }
      },
      "notices": {
        "pendingPayouts": {
          "title": "You have pending payouts to confirm",
          "description": "{{count}} payout(s) are waiting in the manual payout queue.",
          "cta": "Open Manual payout queue"
        }
      },
      "onboarding": {
        "title": "Getting started",
        "description": "Complete these steps to build a reliable financial baseline. Check each step when you are done.",
        "progress": "{{done}} of {{total}} steps complete",
        "status": {
          "notStarted": "Not started",
          "inProgress": "In progress",
          "completed": "Completed"
        },
        "markDone": "Mark as completed",
        "done": "Completed",
        "confirm": {
          "title": "Mark step as completed?",
          "body": "Confirm that you finished: {{step}}",
          "confirm": "Confirm",
          "cancel": "Cancel",
          "success": "Step marked as completed.",
          "error": "Failed to update onboarding status."
        },
        "steps": {
          "profile": {
            "title": "Set up your profile",
            "summary": "Country, currency, timezone, and preferences.",
            "body": [
              "Start with country, base currency, timezone, and preferences.",
              "This is the foundation for accurate calculations, forecasts, and recommendations.",
              "The more precise your profile, the more reliable your financial picture."
            ],
            "imageAlt": "Profile setup illustration",
            "actions": {
              "setupProfile": "Open profile setup"
            }
          },
          "accounts": {
            "title": "Add your accounts",
            "summary": "Create accounts to track real balances.",
            "body": [
              "Add bank accounts and cards to see your real financial position.",
              "Accounts are the source of balance tracking and the full money picture.",
              "If you need precision, open an account and use balance reconciliation."
            ],
            "imageAlt": "Accounts illustration",
            "actions": {
              "manageAccounts": "Open accounts",
              "reconcile": "Reconcile balance"
            }
          },
          "recurring": {
            "title": "Add recurring income & expenses",
            "summary": "Capture your regular cash flow.",
            "body": [
              "Enter all recurring expenses and incomes as accurately as possible.",
              "The platform automatically builds recurring flows from your profile data.",
              "The more effort you invest early, the stronger and more stable your financial foundation becomes."
            ],
            "imageAlt": "Recurring cash flow illustration",
            "actions": {
              "expenses": "Add recurring expenses",
              "income": "Add recurring income"
            }
          },
          "forecast": {
            "title": "Use forecasts and insights",
            "summary": "See future balances and reflect on habits.",
            "body": [
              "Use forecasts to understand future balances and decision impact.",
              "With Insights, study your habits, impulses, and blind spots.",
              "Discuss patterns with AI and find solutions where you feel stuck.",
              "Money management is a long journey of self-knowledge."
            ],
            "imageAlt": "Forecasts and insights illustration",
            "actions": {
              "forecast": "Open forecast",
              "insights": "Open insights"
            }
          },
          "goals": {
            "title": "Set financial goals",
            "summary": "Turn clarity into long-term action.",
            "body": [
              "Goals convert your understanding into real direction and results.",
              "Use the profile, accounts, flows, and insights as your base.",
              "Well-defined goals keep your progress steady and measurable."
            ],
            "imageAlt": "Goals illustration",
            "actions": {
              "goals": "Open goals"
            }
          }
        }
      }
    }
  },
  "uk": {
    "home": {
      "title": "З поверненням",
      "description": "Продовжуйте роботу з обраними сценаріями та перегляньте найближчі дедлайни.",
      "deadlineDateFormat": "DD.MM.YYYY",
      "countdownFormat": "D [днів] HH:mm:ss:SSS",
      "deadlinesTitle": "Топ-5 дедлайнів",
      "deadlinesEmpty": "Поки без дедлайнів",
      "manageDeadlines": "Відкрити керування",
      "nextAction": {
        "title": "Наступна дія",
        "loading": "Завантажуємо підказку онбордингу...",
        "unavailable": "Підказки онбордингу тимчасово недоступні.",
        "none": "Наразі все виконано.",
        "open": "Відкрити"
      },
      "setupProgress": {
        "title": "Прогрес налаштування",
        "loading": "Завантажуємо прогрес...",
        "unavailable": "Прогрес тимчасово недоступний.",
        "summary": "{{done}} / {{total}} виконано",
        "goNext": "Перейти до наступного кроку",
        "help": {
          "buttonAria": "Підказка для кроку: {{step}}",
          "close": "Закрити",
          "accounts": {
            "title": "Додайте базові рахунки",
            "why": "Поки немає обох типів рахунків, інші фінансові сценарії не відкриваються.",
            "steps": [
              "Перейдіть у Accounts і натисніть Add account.",
              "Створіть один bank account.",
              "Створіть один cash account."
            ]
          },
          "liveBalanceAccounts": {
            "title": "Налаштуйте live balance джерела",
            "why": "Live balance синхронізує актуальні суми на рахунках і підвищує точність прогнозу.",
            "steps": [
              "Відкрийте налаштування профілю.",
              "Знайдіть блок Live balance accounts.",
              "Підключіть щонайменше одне джерело й збережіть."
            ]
          },
          "income": {
            "title": "Додайте перший дохід",
            "why": "Доходи формують вхідний потік грошей і потрібні для реалістичного планування.",
            "steps": [
              "Відкрийте Income.",
              "Натисніть Add income і заповніть форму.",
              "Збережіть одну операцію доходу."
            ]
          },
          "expense": {
            "title": "Додайте першу витрату",
            "why": "Витрати формують базу відтоку коштів і відкривають наступні сценарії.",
            "steps": [
              "Відкрийте Expenses.",
              "Натисніть Add expense і вкажіть суму/категорію.",
              "Збережіть одну операцію витрати."
            ]
          },
          "recurring": {
            "title": "Додайте регулярну операцію",
            "why": "Регулярні операції потрібні для довгострокового cash-flow прогнозу.",
            "steps": [
              "Відкрийте Recurring manager.",
              "Створіть recurring income або recurring expense.",
              "Задайте розклад і збережіть."
            ]
          },
          "goals": {
            "title": "Створіть першу фінансову ціль",
            "why": "Цілі перетворюють рух коштів у конкретний довгостроковий результат.",
            "steps": [
              "Відкрийте Finance goals.",
              "Заповніть назву, суму та термін.",
              "Збережіть ціль."
            ]
          },
          "forecast": {
            "title": "Відкрийте прогноз",
            "why": "Це підтверджує, що базові кроки завершені, і відкриває розширені інсайти.",
            "steps": [
              "Відкрийте Forecast з меню або з кнопки на Home.",
              "Дочекайтеся завантаження даних прогнозу.",
              "Перегляньте таймлайн і проєкції."
            ]
          }
        },
        "steps": {
          "accounts": "Додати bank + cash рахунки",
          "liveBalanceAccounts": "Налаштувати рахунки для live balance",
          "income": "Додати перший дохід",
          "expense": "Додати першу витрату",
          "recurring": "Додати першу регулярну операцію",
          "goals": "Створити першу ціль",
          "forecast": "Відкрити прогноз"
        }
      },
      "notices": {
        "pendingPayouts": {
          "title": "У вас є виплати, які треба підтвердити",
          "description": "{{count}} виплата(и) очікують у черзі ручних підтверджень.",
          "cta": "Відкрити Manual payout queue"
        }
      },
      "onboarding": {
        "title": "Початок роботи",
        "description": "Пройдіть ці кроки, щоб створити надійну фінансову базу. Позначайте кроки після виконання.",
        "progress": "{{done}} з {{total}} кроків виконано",
        "status": {
          "notStarted": "Не розпочато",
          "inProgress": "У процесі",
          "completed": "Завершено"
        },
        "markDone": "Позначити виконаним",
        "done": "Виконано",
        "confirm": {
          "title": "Позначити крок виконаним?",
          "body": "Підтвердіть, що ви завершили: {{step}}",
          "confirm": "Підтвердити",
          "cancel": "Скасувати",
          "success": "Крок позначено виконаним.",
          "error": "Не вдалося оновити статус онбордингу."
        },
        "steps": {
          "profile": {
            "title": "Налаштуйте профіль",
            "summary": "Країна, валюта, часовий пояс і вподобання.",
            "body": [
              "Почніть із країни, базової валюти, часового поясу й уподобань.",
              "Це фундамент точних розрахунків, прогнозів і рекомендацій.",
              "Чим точніший профіль, тим надійніша фінансова картина."
            ],
            "imageAlt": "Ілюстрація налаштування профілю",
            "actions": {
              "setupProfile": "Відкрити налаштування"
            }
          },
          "accounts": {
            "title": "Додайте рахунки",
            "summary": "Створіть рахунки для реальних балансів.",
            "body": [
              "Додайте банківські рахунки та картки, щоб бачити реальне положення.",
              "Рахунки потрібні для відстеження балансів і всієї картини грошей.",
              "Якщо потрібна точність — використайте звірку балансу."
            ],
            "imageAlt": "Ілюстрація рахунків",
            "actions": {
              "manageAccounts": "Відкрити рахунки",
              "reconcile": "Звірити баланс"
            }
          },
          "recurring": {
            "title": "Додайте регулярні доходи й витрати",
            "summary": "Зафіксуйте регулярний кешфлоу.",
            "body": [
              "Внесіть регулярні витрати й доходи максимально точно.",
              "Платформа автоматично створює регулярні потоки з даних профілю.",
              "Чим більше зусиль на старті, тим сильніша й стійкіша фінансова основа."
            ],
            "imageAlt": "Ілюстрація регулярних потоків",
            "actions": {
              "expenses": "Додати витрати",
              "income": "Додати доходи"
            }
          },
          "forecast": {
            "title": "Прогнози та інсайти",
            "summary": "Бачте майбутні баланси й досліджуйте звички.",
            "body": [
              "Використовуйте прогнози, щоб розуміти майбутні баланси й наслідки рішень.",
              "Через Інсайти вивчайте свої звички, імпульси та сліпі зони.",
              "Обговорюйте патерни з AI і знаходьте рішення там, де є зупинки.",
              "Управління грошима — довгий шлях самопізнання."
            ],
            "imageAlt": "Ілюстрація прогнозів і інсайтів",
            "actions": {
              "forecast": "Відкрити прогноз",
              "insights": "Відкрити інсайти"
            }
          },
          "goals": {
            "title": "Поставте фінансові цілі",
            "summary": "Перетворіть розуміння на довгострокові дії.",
            "body": [
              "Цілі перетворюють ясність у напрям і результат.",
              "Спирайтеся на профіль, рахунки, потоки, прогнози та інсайти.",
              "Добре визначені цілі тримають прогрес стабільним і вимірюваним."
            ],
            "imageAlt": "Ілюстрація фінансових цілей",
            "actions": {
              "goals": "Відкрити цілі"
            }
          }
        }
      }
    }
  }
};

export default resources;
